var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SuggestedTimesButton } from './SuggestedTimesButton';
import { SuggestedTimesPrompt } from './SuggestedTimesPrompt';
import { TimeSuggestions } from './TimeSuggestionsDrawer';
import { useSuggestedMeetingTimes } from './useSuggestedMeetingTimes';
import { formatQuickPickLabel, pickDistinctDaySuggestions, } from './mapSuggestions';
import { useInviteeAvailability } from '../../../hooks/useInviteeAvailability';
import { useEventDateTime } from '../../../hooks/useDateTime';
import { useEventComposerGlobalContext } from '../../../composers/EventComposerGlobalContext';
import { ComposerAnalyticsEvent, } from '../../../types/index';
/** The proactive prompt only appears above this invitee count. */
var MIN_INVITEES_FOR_PROMPT = 2;
/** How many all-available slots to surface as quick-pick pills. */
var MAX_QUICK_PICKS = 2;
/**
 * Debounce for re-fetching on invitee/date/time edits. The query is real-time
 * and uncached, and a single interaction can commit more than one change (e.g.
 * the range picker's push-adjust sets both start and end), so collapse rapid
 * changes into one request. The mount fetch is exempt — it fires immediately.
 */
var SUGGESTIONS_DEBOUNCE_MS = 400;
/**
 * Owns the suggestions UI state. Pre-fetches suggestions on mount so the data
 * is ready before the drawer opens, and decides which entry surface to show:
 *
 * - the proactive {@link SuggestedTimesPrompt} when there are more than 3
 *   invitees, at least one has a conflict, and we have all-available picks;
 * - otherwise the {@link SuggestedTimesButton};
 * - the {@link TimeSuggestions} drawer once opened.
 */
export var TimeSuggestionsContainer = function () {
    var t = useTranslation('meetingRooms').t;
    var _a = useEventComposerGlobalContext(), timeFormat = _a.timeFormat, timezone = _a.timezone, backgrounded = _a.backgrounded, composerMode = _a.composerMode, onTrackComposerEvent = _a.onTrackComposerEvent;
    var applyDateTimeRange = useEventDateTime({ timezone: timezone }).applyDateTimeRange;
    var _b = __read(useState(false), 2), showTimeSuggestions = _b[0], setShowTimeSuggestions = _b[1];
    var _c = __read(useState(false), 2), promptDismissed = _c[0], setPromptDismissed = _c[1];
    // Shared open handler so both entry surfaces (prompt "more" and the entry
    // button) report the drawer opening.
    var openTimeSuggestions = useCallback(function () {
        onTrackComposerEvent === null || onTrackComposerEvent === void 0 ? void 0 : onTrackComposerEvent(ComposerAnalyticsEvent.OPENED_SUGGESTED_TIMES_SIDER, { mode: composerMode });
        setShowTimeSuggestions(true);
    }, [onTrackComposerEvent, composerMode]);
    // The drawer portals to document.body, so when the host backgrounds the
    // composer (e.g. the office space-preview flow takes the sider slot) it
    // would keep floating over the sider that replaced us — close it.
    useEffect(function () {
        if (backgrounded) {
            setShowTimeSuggestions(false);
        }
    }, [backgrounded]);
    var _d = useSuggestedMeetingTimes(), fetchSuggestions = _d.fetchSuggestions, outcome = _d.outcome, loading = _d.loading;
    var inviteesWithAvailability = useInviteeAvailability({
        hideInvitees: false,
    }).inviteesWithAvailability;
    var _e = __read(useWatch({ name: ['startTime', 'endTime', 'date'] }), 3), startTime = _e[0], endTime = _e[1], date = _e[2];
    // Stable key over the current invitee email set — changes only when an
    // invitee is added or removed (not when free/busy results arrive).
    var inviteeKey = inviteesWithAvailability
        .map(function (invitee) { var _a; return (_a = invitee.email) === null || _a === void 0 ? void 0 : _a.toLowerCase(); })
        .filter(Boolean)
        .join('|');
    // Stable key over the selected date/time — changes when the user edits them.
    var timeKey = [
        startTime === null || startTime === void 0 ? void 0 : startTime.valueOf(),
        endTime === null || endTime === void 0 ? void 0 : endTime.valueOf(),
        date === null || date === void 0 ? void 0 : date.valueOf(),
    ].join('|');
    // Fetch immediately on mount, then debounce re-fetches as invitees or the
    // date/time change so a burst of edits collapses into a single request.
    var hasFetchedOnMount = useRef(false);
    useEffect(function () {
        if (!hasFetchedOnMount.current) {
            hasFetchedOnMount.current = true;
            fetchSuggestions();
            return;
        }
        var handle = setTimeout(fetchSuggestions, SUGGESTIONS_DEBOUNCE_MS);
        return function () { return clearTimeout(handle); };
    }, [fetchSuggestions, inviteeKey, timeKey]);
    var suggestionPills = useMemo(function () {
        return outcome.status === 'success'
            ? pickDistinctDaySuggestions(outcome.available, MAX_QUICK_PICKS).map(function (suggestion) { return ({
                id: suggestion.id,
                label: formatQuickPickLabel(suggestion.startTime, timeFormat, t('time_suggestions.today')),
            }); })
            : [];
    }, [outcome, timeFormat, t]);
    // Proactive prompt: > 2 invitees and at least one has a current conflict.
    var inviteeHasConflict = useMemo(function () {
        return inviteesWithAvailability.some(function (invitee) { return invitee.conflicts.length > 0; });
    }, [inviteesWithAvailability]);
    // Prompt eligibility, independent of whether suggestions have arrived yet:
    // > 2 invitees with at least one conflict. Derived from availability, which
    // is retained across an invitee add, so this stays stable during a refetch.
    var promptEligible = !promptDismissed &&
        inviteesWithAvailability.length > MIN_INVITEES_FOR_PROMPT &&
        inviteeHasConflict;
    // while a refetch is in flight (e.g. an attendee was added) keep the
    // prompt mounted and let it show skeleton pills, rather than collapsing back
    // to the entry button. Otherwise it shows once real pills exist.
    var promptLoading = promptEligible && loading;
    var shouldShowPrompt = promptEligible && (loading || suggestionPills.length > 0);
    // Apply a chosen slot to the event's date/start/end.
    var applySuggestion = useCallback(function (suggestion) {
        applyDateTimeRange(moment.tz(suggestion.startTime, timezone), moment.tz(suggestion.endTime, timezone));
    }, [applyDateTimeRange, timezone]);
    // Prompt pills carry only an id (the slot's start time); resolve the full
    // slot from the current result, apply it, then dismiss the prompt.
    var handlePromptSelect = useCallback(function (id) {
        var suggestion = outcome.status === 'success'
            ? outcome.available.find(function (s) { return s.id === id; })
            : undefined;
        if (suggestion) {
            applySuggestion(suggestion);
            onTrackComposerEvent === null || onTrackComposerEvent === void 0 ? void 0 : onTrackComposerEvent(ComposerAnalyticsEvent.SELECTED_SUGGESTED_TIME, {
                surface: 'prompt',
            });
        }
        setPromptDismissed(true);
    }, [outcome, applySuggestion, onTrackComposerEvent]);
    // Drawer apply-on-click: set the time and close the drawer.
    var handleDrawerApply = useCallback(function (suggestion) {
        applySuggestion(suggestion);
        onTrackComposerEvent === null || onTrackComposerEvent === void 0 ? void 0 : onTrackComposerEvent(ComposerAnalyticsEvent.SELECTED_SUGGESTED_TIME, {
            surface: 'drawer',
        });
        setShowTimeSuggestions(false);
    }, [applySuggestion, onTrackComposerEvent]);
    // The inline entry surface (prompt or button). Kept mounted even while the
    // drawer is open so it doesn't disappear from the composer when the sider
    // opens (QA #1) — the drawer is a non-masking portal that floats over it.
    //
    // The two-pill prompt shows only when there's a conflict to resolve (see
    // `shouldShowPrompt`); otherwise the entry button is always visible —
    // suggestions are available even for a single invitee / the organizer alone.
    var inlineSurface = shouldShowPrompt ? (_jsx(SuggestedTimesPrompt, { suggestions: suggestionPills, quickPickCount: MAX_QUICK_PICKS, loading: promptLoading, onSelect: handlePromptSelect, onClose: function () { return setPromptDismissed(true); }, onMore: openTimeSuggestions })) : (_jsx(SuggestedTimesButton, { onClick: openTimeSuggestions }));
    return (_jsxs(_Fragment, { children: [inlineSurface, showTimeSuggestions && (_jsx(TimeSuggestions, { outcome: outcome, onApply: handleDrawerApply, onClose: function () { return setShowTimeSuggestions(false); } }))] }));
};
